import { HTTP_INTERCEPTORS } from '@angular/common/http';
import '@angular/common/locales/global/ru';
import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, LOCALE_ID, NgModule, Provider } from '@angular/core';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getMessaging, provideMessaging } from '@angular/fire/messaging';
import { FormsModule } from '@angular/forms';
import { BrowserModule, HAMMER_GESTURE_CONFIG, HammerModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouteReuseStrategy, Router } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';

import { IonicModule, IonicRouteStrategy, NavParams } from '@ionic/angular';

import { FingerprintAIO } from '@awesome-cordova-plugins/fingerprint-aio/ngx';
import * as SentryAngular from '@sentry/angular';
import { HttpClientInMemoryWebApiModule } from 'angular-in-memory-web-api';

import { BadNetworkProtectionInterceptor } from '@app/interceptors/bad‑network‑protection.interceptor';

import { CoreCommandsModule } from '@core/core-commands.module';
import { CoreModule } from '@core/core.module';
import { ApiErrorsService } from '@core/services/api/api-errors.service';
import { B2pService } from '@core/services/b2p/b2p.service';
import { CorrespondenceService } from '@core/services/correspondence/correspondence.service';
import { DocumentSettingsService } from '@core/services/document/document-settings.service';
import { DocumentService } from '@core/services/document/document.service';
import { ExchangeRatesService } from '@core/services/exchange-rates/exchange-rates.service';
import { IonicGestureConfig } from '@core/services/ionic-gesture.config';
import { ModalsService } from '@core/services/modals/modals.service';
import { OnlineCreditService } from '@core/services/online-credit/online-credit.service';
import { StepManagerService } from '@core/services/online-credit/step-manager.service';
import { OperationsActionsService } from '@core/services/operations/operations-actions.service';
import { OperationsService } from '@core/services/operations/operations.service';
import { OperationsStore } from '@core/services/operations/operations.store';
import { PrepareOperationsService } from '@core/services/operations/prepare-operations.service';
import { PaymentsService } from '@core/services/payments/payments.service';
import { ProductStatementService } from '@core/services/products/product-statement.service';
import { ProductsServiceRefactored } from '@core/services/products/products-refactored.service';
import { ProductsService } from '@core/services/products/products.service';
import { providePushNotifications } from '@core/services/push-notifications/push-notifications.provider';
import { ServerSettingsService } from '@core/services/server-settings/server-settings.service';
import { SettingsService } from '@core/services/settings/settings.service';
import { SweetCardService } from '@core/services/sweetcard/sweetcard.service';
import { TemplatesService } from '@core/services/templates/templates.service';
import { TransferCurrencyService } from '@core/services/transfer-currency/transfer-currency.service';
import { DecodeUtilService } from '@core/services/utils/decode-util.service';
import { UtilsService } from '@core/services/utils/utils.service';

import { ChatService } from '@pages/chat/services/chat.service';
import { ChoiceOfficeFormService } from '@pages/new-product/new-credit/choice-office-step/choice-office-form.service';
import { LoanCalculationService } from '@pages/new-product/new-credit/loan-calculation-step/loan-calculation.service';
import { PersonalDataFormService } from '@pages/new-product/new-credit/personal-data-step/personal-data-form.service';
import { SigningOfAgreementsStepService } from '@pages/new-product/new-credit/signing-of-agreements-step/signing-of-agreements-step.service';
import { SelffreePage } from '@pages/payments/components/selffree/selffree.page';
import { SelffreeService } from '@pages/payments/components/selffree/selffree.service';
import { ContactsService } from '@pages/payments/components/transfer/services/contacts.service';
import { GisGmpService } from '@pages/payments/components/transfer/services/gis-gmp.service';
import { PaymentDataService } from '@pages/payments/components/transfer/services/payment-data.service';
import { TransferValidationService } from '@pages/payments/components/transfer/services/transfer-validation.service';
import { TransferService } from '@pages/payments/components/transfer/services/transfer.service';
import { AdditionalCardService } from '@pages/product/additional-card/_services/additional-card.service';
import { ThreeDSecureService } from '@pages/product/card-actions/_services/three-d-secure.service';
import { CardLimitService } from '@pages/product/card-limit/_services/card-limit.service';

import { MortgageAgreementComponent } from '@modals/mortgage-areement/mortgage-agreement.component';
import { MortgageInsuranceRequestSuccessComponent } from '@modals/mortgage-insurance-request-success/mortgage-insurance-request-success.component';
import { ProductsListModalComponent } from '@modals/products/products-list-modal.component';
import { TemplatesModule } from '@modals/templates/templates.module';

import { AvatarComponent } from '@shared/components/avatar/avatar.component';
import { BannerCommandsModule } from '@shared/modules/banner-commands.module';
import { CardCurrencyPipe } from '@shared/pipes/card-currency.pipe';
import { SharedModule } from '@shared/shared.module';

import { environment } from '../environments/environment';
import { InMemoryDataService } from './_mocks/in-memory-data.service';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AuthInterceptor } from './interceptors/auth.interceptor';
import { HostFailoverInterceptor } from './interceptors/host-failover.interceptor';
import { XAppVersionInterceptor } from './interceptors/x-app-version.interceptor';


const AuthInterceptorProvider: Provider = {
  provide: HTTP_INTERCEPTORS,
  multi: true,
  useClass: AuthInterceptor
};

const XAppVersionProvider: Provider = {
  provide: HTTP_INTERCEPTORS,
  multi: true,
  useClass: XAppVersionInterceptor
};

const HostFailoverInterceptorProvider: Provider = {
  provide: HTTP_INTERCEPTORS,
  multi: true,
  useClass: HostFailoverInterceptor
};

const sentryProviders: Provider[] = [
  {
    provide: ErrorHandler,
    useValue: SentryAngular.createErrorHandler()
  },
  {
    provide: SentryAngular.TraceService,
    deps: [Router]
  },
  {
    provide: APP_INITIALIZER,
    useFactory: () => () => {},
    deps: [SentryAngular.TraceService],
    multi: true
  }
];

const BadNetworkProtectionInterceptorProvider: Provider = {
  provide: HTTP_INTERCEPTORS,
  multi: true,
  useClass: BadNetworkProtectionInterceptor
};

const oldServices = [
  UtilsService,
  SettingsService,
  TemplatesService,
  ProductsService,
  ApiErrorsService,
  ServerSettingsService,
  ChatService,
  ExchangeRatesService,
  B2pService,
  OperationsService,
  OperationsStore,
  PrepareOperationsService,
  DocumentService,
  OperationsActionsService,
  SelffreeService,
  SweetCardService,
  OnlineCreditService,
  PaymentsService,
  ThreeDSecureService,
  CardLimitService,
  ProductStatementService,
  TransferCurrencyService,
  StepManagerService,
  SigningOfAgreementsStepService,
  PersonalDataFormService,
  LoanCalculationService,
  ChoiceOfficeFormService,

  ModalsService,
  CorrespondenceService,
  DocumentSettingsService,
  AdditionalCardService,

  TransferService,
  TransferValidationService,
  GisGmpService,
  PaymentDataService,
  ContactsService,
  DecodeUtilService,
  CardCurrencyPipe
];

// Импортируем только в DEV режиме
const devImports = [];

if (!environment.production && environment?.accountSecurity?.useInMemoryApi) {
  devImports.push(
    HttpClientInMemoryWebApiModule.forRoot(InMemoryDataService, {
      apiBase: '/', // Базовый путь для моков
      passThruUnknownUrl: true, // Пропускать немиокируемые URL
      delay: 300 // Задержка для имитации сети
    })
  );
}

function provideFirebaseMessaging() {
  const opts = environment.firebaseOpts;

  return [provideFirebaseApp(() => initializeApp(opts)), provideMessaging(() => getMessaging())];
}

@NgModule({
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  imports: [
    BrowserModule,
    CoreModule,
    CoreCommandsModule,
    SharedModule,
    AppRoutingModule,
    FormsModule,
    TemplatesModule,
    BrowserAnimationsModule,
    HammerModule,
    BannerCommandsModule,
    IonicModule.forRoot({
      swipeBackEnabled: false,
      innerHTMLTemplatesEnabled: true,
      mode: 'ios'
    }),
    ServiceWorkerModule.register('metib-sw.js', {
      enabled: environment.pwa?.enabled === true,
      registrationStrategy: 'registerWhenStable:30000'
    })
    // ...devImports // Моки для тестового ENV
  ],
  declarations: [
    AppComponent,
    SelffreePage,
    ProductsListModalComponent,
    MortgageAgreementComponent,
    MortgageInsuranceRequestSuccessComponent
  ],
  providers: [
    {
      provide: RouteReuseStrategy,
      useClass: IonicRouteStrategy
    },
    { provide: LOCALE_ID, useValue: 'ru-RU' },
    {
      provide: HAMMER_GESTURE_CONFIG,
      useClass: IonicGestureConfig
    },
    // Sentry провайдеры (только в production)
    ...sentryProviders,
    {
      provide: ProductsService,
      useClass: ProductsServiceRefactored
    },
    // {
    //   provide: HTTP_INTERCEPTORS,
    //   useClass: ErrorSimulationInterceptor,
    //   multi: true,
    // },
    // {
    //
    AuthInterceptorProvider,
    XAppVersionProvider,
    HostFailoverInterceptorProvider,
    BadNetworkProtectionInterceptorProvider,
    ...oldServices,
    FingerprintAIO,
    NavParams,
    DecodeUtilService,
    provideFirebaseMessaging(),
    providePushNotifications()
  ],
  exports: [AvatarComponent],
  bootstrap: [AppComponent]
})
export class AppModule {}
